import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useSeo, MANUFACTURER_URL } from "@/lib/site";
import { Reveal, MaskLines } from "@/components/Reveal";

export default function Catalogue() {
  useSeo("Block Catalogue — Breeze Craft Sydney", "Browse architectural breeze block profiles we supply and install across Sydney: dimensions, finishes, colours and typical uses.");
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blocks").then((r) => setBlocks(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 sm:py-20" data-testid="catalogue-page">
      <p className="kicker mb-4">Catalogue</p>
      <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-ink" data-testid="catalogue-heading">
        <MaskLines lines={["Every profile,", "in stock or en route."]} />
      </h1>
      <Reveal delay={0.2} className="mt-5 max-w-2xl">
        <p className="text-neutral-600 leading-relaxed">
          Eight breeze block patterns we supply and install, in Porcelain, Pewter, Pottery and Nickel. Tap a pattern for dimensions, finishes and where it works best.
        </p>
        <p className="mt-3 text-sm text-neutral-500 leading-relaxed" data-testid="catalogue-manufacturer-note">
          Blocks are sourced from Australian manufacturers including Austral Masonry.{" "}
          <a href={MANUFACTURER_URL} target="_blank" rel="noopener noreferrer" data-testid="manufacturer-catalogue-link" className="underline underline-offset-4 text-ink hover:text-terracotta transition-colors">
            View the full manufacturer range
          </a>
          .
        </p>
      </Reveal>

      {loading ? (
        <p className="mt-16 text-sm text-neutral-500" data-testid="catalogue-loading">Loading catalogue…</p>
      ) : (
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10" data-testid="catalogue-grid">
          {blocks.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.05}>
              <Link to={`/catalogue/${b.slug}`} data-testid={`block-card-${b.slug}`} className="group block bg-bone p-5 h-full">
                <div className="aspect-square overflow-hidden relative">
                  <img src={imgSrc(b.images[0])} alt={`${b.name} breeze block`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 bg-bone px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-soft">{b.dimensions}</span>
                </div>
                <div className="mt-4 flex items-start justify-between">
                  <div>
                    <p className="font-display text-xl font-medium text-ink">{b.name}</p>
                    <p className="text-xs text-neutral-500 mt-1">{b.brand} · {b.pattern}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {b.colours.map((c) => (
                        <span key={c} className="border border-ink/15 px-2 py-0.5 text-[11px] text-neutral-600">{c}</span>
                      ))}
                    </div>
                  </div>
                  <ArrowUpRight size={18} className="text-neutral-400 group-hover:text-terracotta transition-colors shrink-0 mt-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
      <p className="mt-12 text-xs text-neutral-500 leading-relaxed max-w-2xl" data-testid="catalogue-disclaimer">
        Breeze Craft is an independent supplier and installer and is not affiliated with or endorsed by Austral Masonry. Product images are Breeze Craft illustrations of each pattern; refer to the manufacturer for exact specifications.
      </p>
    </div>
  );
}
